/*
 * The transport of one upload: `PUT /api/uploads/{clientUuid}` with its metadata headers, the
 * progress the person watches, and the two cheap verbs around it (spec 07 §3.1).
 *
 * It is XHR and not `fetch` for one reason: only XHR reports how much of a request body has left
 * the browser. Everything else about the call is the same as `core/net/api.ts` makes it — same
 * origin, same synchroniser token, the same error shape — and the two error paths meet again in
 * `errorOf`, which reads the server's `{"error": …}` body into the key the chip renders.
 *
 * The token is the one `core/net/api.ts` holds and no copy of it: this file asks for the value per
 * request and, when a request is refused for the token alone, re-reads the session into that same
 * place (I43). A second cache here could only ever be the stale one — it was read once per tab and
 * never learned about a session renewed in another one.
 */

import { api, CSRF_HEADER, csrfValue, setCsrfToken } from "../../net/api.ts"
import type { AttachmentWire } from "../../wire/index.ts"
import type { StagedError, UploadScope } from "./types.ts"

const NAME_HEADER = "X-Upload-Name"
const SCOPE_HEADER = "X-Upload-Scope"

/** The record every upload verb answers: the attachment as every other surface receives it. */
export type UploadRecord = AttachmentWire

/**
 * Re-reads the session's synchroniser token into the one place it is kept. An anonymous answer
 * leaves it empty on purpose: the pre-session double-submit cookie is what serves the four
 * anonymous routes, and `csrfValue` falls through to it while the in-memory value is empty
 * (security.md §5.1).
 */
async function refreshCsrf(): Promise<string> {
	const session = await api.get<{ user?: unknown; csrfToken?: string }>("/api/auth/session")
	setCsrfToken(session.user ? (session.csrfToken ?? "") : "")
	return csrfValue()
}

/** The name header of RFC 5987: percent-encoded UTF-8, so a German or Japanese name survives. */
function encodeUploadName(name: string): string {
	return `UTF-8''${encodeURIComponent(name)}`
}

/**
 * A failure of an upload. It is an `Error` because a rejected promise must be one, and it carries
 * the three fields a chip renders: the server's locale key, the numbers its sentence names, and
 * whether another attempt could ever succeed.
 */
export class UploadError extends Error implements StagedError {
	readonly key: string
	readonly args?: Record<string, string | number>
	readonly retryable: boolean

	constructor(key: string, retryable: boolean, args?: Record<string, string | number>) {
		super(key)
		this.name = "UploadError"
		this.key = key
		this.retryable = retryable
		if (args !== undefined) this.args = args
	}
}

/** Reads the server's `{"error": …}` body into the failure the chip shows. */
function errorOf(status: number, body: string): UploadError {
	let key = ""
	let args: Record<string, string | number> | undefined
	try {
		const parsed = JSON.parse(body) as {
			error?: { key?: string; args?: Record<string, string | number> }
		}
		key = parsed.error?.key ?? ""
		args = parsed.error?.args
	} catch {
		// A refusal without a JSON body — a proxy's 502, an aborted answer — still needs a shape.
	}
	// A network failure and a server failure are both worth a second attempt; a refusal that names
	// a rule the request broke is not, whatever the person does to the same bytes.
	const retryable = status === 0 || status === 429 || status >= 500
	return new UploadError(key !== "" ? key : "error.network", retryable, args)
}

/**
 * A scope a module registered for its uploads (`r.UploadScope` on the server): its prefix, a colon and
 * what the module reads after it. Core never names one; the server checks it.
 */
export type ModuleUploadScope = `${string}:${string}`

/**
 * Where an upload belongs. It is a composer's draft target key (`c:<id>`, `c:<id>:t:<root>`,
 * `e:<messageId>`), the literal `avatar` — the account's own picture is the one upload that is not
 * a message's and never lives in a draft —, or a scope a module registered. The server parses the
 * same shapes (`files/ingest.go`'s `parseUploadScope`, and the scopes the modules register).
 */
export type UploadTarget = UploadScope | "avatar" | ModuleUploadScope

export interface UploadOptions {
	uuid: string
	scope: UploadTarget
	file: File
	/** Called with 0…1 as the body leaves the browser. */
	onProgress?: (fraction: number) => void
	/** Aborting it aborts the request; the caller then also cancels the upload on the server. */
	signal?: AbortSignal
}

/**
 * Sends the bytes. It resolves with the stored record and rejects with an `UploadError`: every
 * caller of this file renders a locale key, and a bare `TypeError` has none.
 */
export function putUpload(options: UploadOptions): Promise<UploadRecord> {
	return withToken((csrf) => sendOnce(options, csrf))
}

/**
 * Runs one request with the session's token, and exactly once more with a freshly read one when the
 * first attempt was refused for the token alone. Anything else is handed on as it is.
 */
async function withToken<T>(run: (csrf: string) => Promise<T>): Promise<T> {
	try {
		return await run(csrfValue())
	} catch (err) {
		if (!(err instanceof UploadError) || err.key !== "error.policy.csrf") throw err
		return await run(await refreshCsrf())
	}
}

function sendOnce(options: UploadOptions, csrf: string): Promise<UploadRecord> {
	const { uuid, scope, file, onProgress, signal } = options
	return new Promise<UploadRecord>((resolve, reject) => {
		const request = new XMLHttpRequest()
		request.open("PUT", `/api/uploads/${encodeURIComponent(uuid)}`)
		request.withCredentials = true
		request.setRequestHeader(CSRF_HEADER, csrf)
		request.setRequestHeader(NAME_HEADER, encodeUploadName(file.name))
		request.setRequestHeader(SCOPE_HEADER, scope)
		request.responseType = "text"

		request.upload.onprogress = (event) => {
			if (event.lengthComputable && event.total > 0) onProgress?.(event.loaded / event.total)
		}
		request.onload = () => {
			if (request.status >= 200 && request.status < 300) {
				try {
					resolve(JSON.parse(request.responseText) as UploadRecord)
				} catch {
					reject(errorOf(0, ""))
				}
				return
			}
			reject(errorOf(request.status, request.responseText))
		}
		request.onerror = () => reject(errorOf(0, ""))
		request.ontimeout = () => reject(errorOf(0, ""))
		request.onabort = () => reject(new UploadError("composer.attach.cancelled", false))

		if (signal !== undefined) {
			if (signal.aborted) {
				request.abort()
				return
			}
			signal.addEventListener("abort", () => request.abort(), { once: true })
		}
		request.send(file)
	})
}

/**
 * The record of an upload the server may already hold, or `undefined` when it does not.
 *
 * This is what makes the one auto-retry cheap: a connection that dropped after the last byte left
 * the browser has already stored the file, and re-sending a hundred megabytes to learn that would
 * be the worst possible answer.
 */
export async function headUpload(uuid: string): Promise<UploadRecord | undefined> {
	try {
		return await api.get<UploadRecord>(`/api/uploads/${encodeURIComponent(uuid)}`)
	} catch {
		return undefined
	}
}

/** Cancels an upload: the pending row goes and the reserved budget is released. */
export async function cancelUpload(uuid: string): Promise<void> {
	try {
		await api.del<void>(`/api/uploads/${encodeURIComponent(uuid)}`)
	} catch {
		// A cancel that does not arrive costs a pending row the 30-day draft sweep removes; it must
		// never keep the chip on screen or fail the person's next action.
	}
}
